import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DynamicAttributes, OccEndpointsService } from '@spartacus/core';
import { Observable } from 'rxjs';
import { pluck } from 'rxjs/operators';
import {
  CartActionSource,
  CartActionSourceData,
  CartModification,
  MultipleArticlesAddedResponse,
  OrderEntry,
  Unit,
} from '../../../../core/model';
import { Occ } from '../../../../core/occ';
import { PyAddToCart } from '../../../../core/occ/occ-models/occ-generated.models';
import { ConverterService } from '../../../../core/util';
import { TraceCrumbCode } from '../../../tracking/models/ga4.model';
import { CART_MODIFICATION_NORMALIZER, ORDER_ENTRY_SERIALIZER } from './converters';

@Injectable({
  providedIn: 'root',
})
export class CartEntryConnector {
  constructor(
    private httpClient: HttpClient,
    private occEndpointsService: OccEndpointsService,
    private converterService: ConverterService
  ) {}

  add(
    userId: string,
    cartId: string,
    articleCode: string,
    quantity: number,
    unit: Unit,
    orderCardCode?: string,
    customerMaterialNumber?: string,
    cartTemplate?: string,
    itemListType?: string
  ): Observable<CartModification> {
    const body: PyAddToCart = {
      articleNumber: articleCode,
      quantity,
      unit: unit.code,
      orderCardCode,
      customerMaterialNumber,
      cartTemplate,
      traceCrumbs: [{ code: TraceCrumbCode.GA4_ECOM_ITEM_LIST_ATTRIBUTION, value: itemListType }],
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    const url = this.occEndpointsService.buildUrl('addCartEntry', {
      urlParams: { userId, cartCodeOrType: cartId },
    });
    return this.httpClient
      .patch<Occ.CartModification>(url, body, { headers })
      .pipe(this.converterService.pipeable(CART_MODIFICATION_NORMALIZER));
  }

  addA4Sample(
    userId: string,
    cartId: string,
    productCode: string,
    grammage: number,
    color: string,
    quantity: number
  ): Observable<CartModification> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    const url = this.occEndpointsService.buildUrl('addA4SampleCartEntry', {
      urlParams: { userId: userId, cartCodeOrType: cartId },
      queryParams: { productId: productCode, grammage, color, quantity },
    });
    return this.httpClient
      .patch<Occ.CartModification>(url, {}, { headers })
      .pipe(this.converterService.pipeable(CART_MODIFICATION_NORMALIZER));
  }

  quickAdd(
    userId: string,
    cartId: string,
    articleCode: string,
    quantity: number,
    unit: Unit,
    note?: string,
    customerMaterialNumber?: string,
    itemListType?: string
  ): Observable<CartModification> {
    const body: PyAddToCart = {
      articleNumber: articleCode,
      quantity,
      unit: unit.code,
      customerMaterialNumber,
      note,
      traceCrumbs: [{ code: TraceCrumbCode.GA4_ECOM_ITEM_LIST_ATTRIBUTION, value: itemListType }],
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    const url = this.occEndpointsService.buildUrl('quickAddCartEntry', {
      urlParams: { userId, cartCodeOrType: cartId },
    });
    return this.httpClient
      .patch<Occ.CartModification>(url, body, { headers })
      .pipe(this.converterService.pipeable(CART_MODIFICATION_NORMALIZER));
  }

  addMultiple(
    userId: string,
    cartId: string,
    entries: OrderEntry[],
    sourceData?: CartActionSourceData,
    itemListType?: string
  ): Observable<MultipleArticlesAddedResponse> {
    const attributes: DynamicAttributes = {
      urlParams: {
        userId: userId,
        cartCodeOrType: cartId,
      },
    };

    if (CartActionSource.SHOPPING_LIST === sourceData?.source) {
      attributes.queryParams = { cartTemplate: sourceData.sourceId };
    }

    const url = this.occEndpointsService.buildUrl('addCartEntries', attributes);

    return this.httpClient.patch<Occ.MultipleArticlesAddedResponse>(
      url,
      this.converterService.convertMany(entries, ORDER_ENTRY_SERIALIZER).map((entry) => ({
        ...entry,
        traceCrumbs: [{ code: TraceCrumbCode.GA4_ECOM_ITEM_LIST_ATTRIBUTION, value: itemListType }],
      }))
    );
  }

  remove(userId: string, cartId: string, entry: OrderEntry): Observable<CartModification> {
    const url = this.occEndpointsService.buildUrl('cartEntry', {
      urlParams: {
        userId: userId,
        cartCodeOrType: cartId,
        entryNumber: `${entry.entryNumber}`,
      },
    });
    return this.httpClient.delete<Occ.CartModification>(url).pipe(this.converterService.pipeable(CART_MODIFICATION_NORMALIZER));
  }

  removeMultiple(userId: string, cartId: string, entries: OrderEntry[]): Observable<CartModification[]> {
    const url = this.occEndpointsService.buildUrl('cartEntriesMultiple', {
      urlParams: {
        userId: userId,
        cartCodeOrType: cartId,
      },
      queryParams: {
        entryNumbers: entries.map(({ entryNumber }) => `${entryNumber}`).join(','),
      },
    });
    return this.httpClient
      .delete<Occ.CartModificationList>(url)
      .pipe(pluck('cartModifications'), this.converterService.pipeableMany(CART_MODIFICATION_NORMALIZER));
  }

  removeAll(userId: string, cartId: string): Observable<any> {
    const url = this.occEndpointsService.buildUrl('cartEntries', {
      urlParams: {
        userId: userId,
        cartCodeOrType: cartId,
      },
    });
    return this.httpClient.delete<Occ.CartModification>(url);
  }

  update(userId: string, cartId: string, entry: OrderEntry, quantity?: number, unit?: Unit): Observable<CartModification> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    const url = this.occEndpointsService.buildUrl('cartEntry', {
      urlParams: { userId: userId, cartCodeOrType: cartId, entryNumber: `${entry.entryNumber}` },
      queryParams: { quantity, unit: unit.code },
    });
    return this.httpClient
      .patch<Occ.CartModification>(url, {}, { headers })
      .pipe(this.converterService.pipeable(CART_MODIFICATION_NORMALIZER));
  }

  updateNote(note: string, userId: string, cartId: string, entry: OrderEntry): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    const url = this.occEndpointsService.buildUrl('cartEntryNote', {
      urlParams: { userId: userId, cartCodeOrType: cartId, entryNumber: `${entry.entryNumber}` },
      queryParams: { note },
    });
    return this.httpClient.put(url, {}, { headers });
  }

  updateNameMarking(
    userId: string,
    cartId: string,
    entry: OrderEntry,
    qualifier: string,
    ruleId: string,
    extendedIdentifier: string,
    values: string[]
  ): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    const url = this.occEndpointsService.buildUrl('cartEntryNameMarking', {
      urlParams: {
        userId: userId,
        cartCodeOrType: cartId,
        entryNumber: `${entry.entryNumber}`,
        qualifier: qualifier,
        ruleId: ruleId,
        extendedIdentifier: extendedIdentifier,
      },
      queryParams: { values },
    });
    return this.httpClient.patch(url, {}, { headers });
  }

  updateStatisticsCode(statisticsCode: string, userId: string, cartId: string, entry: OrderEntry): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    const url = this.occEndpointsService.buildUrl('cartEntryStatisticsCode', {
      urlParams: { userId: userId, cartCodeOrType: cartId, entryNumber: `${entry.entryNumber}` },
      queryParams: { statisticsCode },
    });
    return this.httpClient.put(url, {}, { headers });
  }

  updateAccountingCode(accountingCode: string, userId: string, cartId: string, entry: OrderEntry): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    const url = this.occEndpointsService.buildUrl('cartEntryAccountingCode', {
      urlParams: { userId: userId, cartCodeOrType: cartId, entryNumber: `${entry.entryNumber}` },
      queryParams: { accountingCode },
    });
    return this.httpClient.patch(url, {}, { headers });
  }

  replace(
    userId: string,
    cartId: string,
    entry: OrderEntry,
    articleNumber: string,
    quantity: number,
    unit: Unit
  ): Observable<CartModification> {
    const url = this.occEndpointsService.buildUrl('cartEntry', {
      urlParams: {
        userId: userId,
        cartCodeOrType: cartId,
        entryNumber: `${entry.entryNumber}`,
      },
      queryParams: {
        articleNumber,
        quantity,
        unit: unit.code,
      },
    });
    return this.httpClient.put<Occ.CartModification>(url, {}).pipe(this.converterService.pipeable(CART_MODIFICATION_NORMALIZER));
  }
}
